import React from 'react'
import { BillingHistoryEntryFragment } from 'gqlTypes'

import { panelActionCellProps } from '../RowGroup'

// Utils
import dateFormat from 'utils/localizedDateFormat'
import { formatPriceShort } from 'utils/money'

// Styles
import {
  BillingRow,
  BillingDescriptionCell,
  BillingDownloadCell,
  BillingHeadingCell,
  Description,
  DiscountCode,
} from './BillingHistoryItem.styles'

interface BillingHistoryItemProps {
  item: BillingHistoryEntryFragment
}

/**
 * BillingHistoryItem
 *
 * Renders each row in the Billing History Panel in Workspace Settings
 */
const BillingHistoryItem: React.FC<BillingHistoryItemProps> = ({ item }) => {
  if (item.__typename === 'CustomerInvoice') {
    const { createdAt, amount, pdf, couponInfo } = item

    const shouldRenderDiscount = !!couponInfo

    return (
      <BillingRow>
        <BillingHeadingCell>
          {dateFormat(new Date(createdAt))}
        </BillingHeadingCell>
        <BillingDescriptionCell>
          <div>
            Total (incl. tax){' '}
            <strong>
              {formatPriceShort(amount, { trimDecimalIfZero: false })}
            </strong>
          </div>
          {shouldRenderDiscount && (
            <div>
              <DiscountCode>{couponInfo!.promotionCode}</DiscountCode> Discount
              applied ({couponInfo!.percentOff}%)
            </div>
          )}
        </BillingDescriptionCell>
        <BillingDownloadCell {...panelActionCellProps}>
          <strong>
            <a href={pdf} download>
              View Invoice
            </a>
          </strong>
        </BillingDownloadCell>
      </BillingRow>
    )
  } else if (
    item.__typename === 'CreditTransaction' &&
    item?.licenseRedemption
  ) {
    const { createdAt, amount, licenseRedemption } = item

    return (
      <BillingRow>
        <BillingHeadingCell>
          {dateFormat(new Date(createdAt))}
        </BillingHeadingCell>
        <BillingDescriptionCell>
          <div>
            Total{' '}
            <strong>
              +{formatPriceShort(amount, { trimDecimalIfZero: false })}
            </strong>{' '}
            credit (redeemed license)
          </div>
          <Description>{licenseRedemption?.serial}</Description>
          <Description>
            $9 x {licenseRedemption.seats} seats x {licenseRedemption.months}{' '}
            months
          </Description>
        </BillingDescriptionCell>
      </BillingRow>
    )
  } else if (item.__typename === 'CreditTransaction') {
    const { createdAt, amount, description } = item

    return (
      <BillingRow>
        <BillingHeadingCell>
          {dateFormat(new Date(createdAt))}
        </BillingHeadingCell>
        <BillingDescriptionCell>
          <div>
            Total{' '}
            <strong>
              {amount > 0 && '+'}
              {formatPriceShort(amount, { trimDecimalIfZero: false })}
            </strong>{' '}
            credit
          </div>
          <Description>{description}</Description>
        </BillingDescriptionCell>
      </BillingRow>
    )
  }

  return null
}

export default BillingHistoryItem
